import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@design-system';
import { QuestionCombobox } from '@/features/quote-and-apply-builder';
import { Plus, X } from 'lucide-react';
import { useSubsectionSettings } from '@/features/quote-and-apply-builder';

interface SubsectionSettingsDialogProps {
  open: boolean;
  subsection: PolicyFormSubSection;
  questions: Record<string, PolicyFormQuestion>;
  onSave: (updates: Partial<PolicyFormSubSection>) => void;
  onClose: () => void;
}

export function SubsectionSettingsDialog({
  open,
  subsection,
  questions,
  onSave,
  onClose,
}: SubsectionSettingsDialogProps) {
  const {
    title,
    setTitle,
    hasCondition,
    setHasCondition,
    conditionType,
    setConditionType,
    simpleCondition,
    setSimpleCondition,
    logicalCondition,
    setLogicalCondition,
    selectedQuestion,
    showValueInput,
    questionOptions,
    comparisonOperators,
    handleSave,
    handleAddLogicalCondition,
    handleUpdateLogicalCondition,
    handleRemoveLogicalCondition,
  } = useSubsectionSettings({ subsection, questions, onSave, onClose });

  return (
    <Dialog
      open={open}
      onOpenChange={onClose}
    >
      <DialogContent className="max-h-[95vh] gap-6 overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Subsection Settings</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="subsection-title">Subsection Title</Label>
            <Input
              value={title}
              onChange={event => setTitle(event.target.value)}
              placeholder="Enter subsection title"
              id="subsection-title"
            />
          </div>

          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="show-hide-rule">Show/Hide Rule</Label>
              <Select
                value={hasCondition ? 'yes' : 'no'}
                onValueChange={value => setHasCondition(value === 'yes')}
              >
                <SelectTrigger
                  id="show-hide-rule"
                  className="w-full"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no">Always Show</SelectItem>
                  <SelectItem value="yes">Conditional</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {hasCondition && (
              <div className="space-y-6 rounded-lg border p-4">
                <div className="space-y-2">
                  <Label htmlFor="rule-type">Rule Type</Label>
                  <Select
                    value={conditionType}
                    onValueChange={value => setConditionType(value as 'simple' | 'logical')}
                  >
                    <SelectTrigger
                      id="rule-type"
                      className="w-full"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="simple">Single Rule</SelectItem>
                      <SelectItem value="logical">Multiple Rules (AND/OR)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {conditionType === 'simple' && (
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <Label>Question</Label>
                      <QuestionCombobox
                        value={simpleCondition.field}
                        options={questionOptions}
                        onChange={val =>
                          setSimpleCondition(prev => ({ ...prev, field: val as string, value: '' }))
                        }
                        className="w-full"
                        placeholder="Select a question..."
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="operator">Operator</Label>
                      <Select
                        value={simpleCondition.operator}
                        onValueChange={op =>
                          setSimpleCondition(prev => ({
                            ...prev,
                            operator: op as ComparisonOperator,
                          }))
                        }
                      >
                        <SelectTrigger
                          id="operator"
                          className="w-full"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {comparisonOperators.map(op => (
                            <SelectItem
                              key={op.value}
                              value={op.value}
                            >
                              {op.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {showValueInput && (
                      <div className="space-y-2">
                        <Label htmlFor="value">Value</Label>
                        {selectedQuestion?.type === 'select' ||
                        selectedQuestion?.type === 'radio' ? (
                          <Select
                            value={(simpleCondition.value as string) || ''}
                            onValueChange={val =>
                              setSimpleCondition(prev => ({ ...prev, value: val }))
                            }
                          >
                            <SelectTrigger
                              id="value"
                              className="w-full"
                            >
                              <SelectValue placeholder="Select value" />
                            </SelectTrigger>
                            <SelectContent>
                              {selectedQuestion.options?.map(opt => (
                                <SelectItem
                                  key={opt.id}
                                  value={opt.id}
                                >
                                  {opt.value}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            value={(simpleCondition.value as string) || ''}
                            onChange={event =>
                              setSimpleCondition(prev => ({ ...prev, value: event.target.value }))
                            }
                            placeholder="Enter value"
                          />
                        )}
                      </div>
                    )}
                  </div>
                )}

                {conditionType === 'logical' && (
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="combined-rules">Combine Rules With</Label>
                      <Select
                        value={logicalCondition.operator}
                        onValueChange={op =>
                          setLogicalCondition(prev => ({ ...prev, operator: op as 'and' | 'or' }))
                        }
                      >
                        <SelectTrigger
                          id="combined-rules"
                          className="w-full"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="and">AND</SelectItem>
                          <SelectItem value="or">OR</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      {logicalCondition.conditions.map((condition, index) => {
                        if ('conditions' in condition) return null;
                        const fieldCondition = condition as FieldCondition;
                        const condQuestion = questions[fieldCondition.field];
                        const showVal = !['empty', 'notEmpty'].includes(fieldCondition.operator);

                        return (
                          <div
                            key={index}
                            className="bg-muted/50 flex items-start gap-2 rounded-lg border p-2"
                          >
                            <div className="flex-1 space-y-2">
                              <QuestionCombobox
                                value={fieldCondition.field}
                                options={questionOptions}
                                onChange={val =>
                                  handleUpdateLogicalCondition(index, {
                                    field: val as string,
                                    value: '',
                                  })
                                }
                                placeholder="Select a question..."
                                className="w-full"
                              />
                              <div className="space-y-2">
                                <Select
                                  value={fieldCondition.operator}
                                  onValueChange={op =>
                                    handleUpdateLogicalCondition(index, {
                                      operator: op as ComparisonOperator,
                                    })
                                  }
                                >
                                  <SelectTrigger className="w-full">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {comparisonOperators.map(op => (
                                      <SelectItem
                                        key={op.value}
                                        value={op.value}
                                      >
                                        {op.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {showVal &&
                                  (condQuestion?.type === 'select' ||
                                  condQuestion?.type === 'radio' ? (
                                    <Select
                                      value={(fieldCondition.value as string) || ''}
                                      onValueChange={val =>
                                        handleUpdateLogicalCondition(index, { value: val })
                                      }
                                    >
                                      <SelectTrigger className="w-full">
                                        <SelectValue placeholder="Select value" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {condQuestion.options?.map(opt => (
                                          <SelectItem
                                            key={opt.id}
                                            value={opt.id}
                                          >
                                            {opt.value}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    <Input
                                      value={(fieldCondition.value as string) || ''}
                                      onChange={event =>
                                        handleUpdateLogicalCondition(index, {
                                          value: event.target.value,
                                        })
                                      }
                                      placeholder="Value"
                                      className="w-full"
                                    />
                                  ))}
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveLogicalCondition(index)}
                            >
                              <X />
                            </Button>
                          </div>
                        );
                      })}
                    </div>

                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={handleAddLogicalCondition}
                    >
                      <Plus />
                      Add Condition
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
